const React = window.React || null;
const { useEffect, useRef } = React;
import NewTabIcon from './NewTabIcon.tsx';
import SignOutIcon from './SignOutIcon.tsx';

type UserNavItem = {
    ID?: number;
    title?: string;
    url?: string;
    target?: string;
    classes?: string[];
};

type UserNavDropdownProps = {
    items: UserNavItem[];
    isOpen: boolean;
};

function UserNavDropdown({ items, isOpen }: UserNavDropdownProps) {
    const refDropdownMenu = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const elDropdownMenu = refDropdownMenu.current;
        if( !elDropdownMenu ){
            return;
        }

        if( 'function' === typeof anime ){
            anime.remove(elDropdownMenu);
            if( isOpen ){
                elDropdownMenu.style.visibility = 'visible';
                elDropdownMenu.style.pointerEvents = 'auto';
                anime({
                    targets: elDropdownMenu,
                    opacity: [0, 1],
                    translateY: [-10, 0],
                    duration: 300,
                    easing: 'easeOutQuad',
                });
                return;
            }

            anime({
                targets: elDropdownMenu,
                opacity: [1, 0],
                translateY: [0, -10],
                duration: 220,
                easing: 'easeInQuad',
                complete: () => {
                    elDropdownMenu.style.visibility = 'hidden';
                    elDropdownMenu.style.pointerEvents = 'none';
                },
            });
            return;
        }

        if( isOpen ){
            elDropdownMenu.style.opacity = '1';
            elDropdownMenu.style.transform = 'translateY(0)';
            elDropdownMenu.style.visibility = 'visible';
            elDropdownMenu.style.pointerEvents = 'auto';
        } else {
            elDropdownMenu.style.opacity = '0';
            elDropdownMenu.style.transform = 'translateY(-10px)';
            elDropdownMenu.style.visibility = 'hidden';
            elDropdownMenu.style.pointerEvents = 'none';
        }
    }, [isOpen]);

    if( items.length === 0 ){
        return null;
    }

    return (
        <div
            ref={refDropdownMenu}
            className="position-absolute end-0 pt-2"
            style={{
                zIndex: 1000,
                minWidth: '230px',
                opacity: 0,
                transform: 'translateY(-10px)',
                visibility: 'hidden',
                pointerEvents: 'none',
            }}
            aria-hidden={!isOpen}
        >
            <ul
                className="list-group rounded-4 shadow-sm mb-0 "
                role="menu"
            >
                { items.map((item: UserNavItem, index: number) => {
                    const itemClasses = item?.classes?.filter(Boolean).join(' ') || '';
                    const resolvedTarget = itemClasses.includes('new-tab') ? '_blank' : item.target;
                    const isNewTabItem = resolvedTarget === '_blank';
                    const titleWords = (item?.title || '').trim().split(/\s+/).filter(Boolean);
                    const lastWord = titleWords.length > 0 ? titleWords[titleWords.length - 1] : '';
                    const leadingWords = titleWords.length > 1 ? titleWords.slice(0, -1).join(' ') : '';
                    const isSignOutItem = item?.title?.toLowerCase?.().includes?.('sign out') || item?.title?.toLowerCase?.().includes?.('logout');
                    const trimmedUrl = item?.url?.trim() ?? '';
                    const itemPath = trimmedUrl && !trimmedUrl.startsWith('#') && !trimmedUrl.startsWith('?')
                        ? new URL(trimmedUrl, location.href).pathname
                        : '';
                    const isActiveItem = Boolean(itemPath) && itemPath === location.pathname;

                    return (
                        <li
                            className={`list-group-item p-0 overflow-hidden ${itemClasses} ${isActiveItem ? ' active' : ''}`}
                            key={`user-nav-item-${item.ID || index}`}
                            role="none"
                        >
                            <a
                                href={item.url}
                                target={resolvedTarget || undefined}
                                rel={isNewTabItem ? 'noopener noreferrer' : undefined}
                                className={`d-inline-flex w-100 fs-6 py-2 px-3 align-items-center flex-wrap gap-1 text-decoration-none ${isActiveItem ? '' : 'text-hover-primary'} ${isSignOutItem ? 'text-danger zephr-logout' : ''}`}
                                onMouseEnter={(event) => !isActiveItem && event.currentTarget.classList.add('bg-light-subtle')}
                                onMouseLeave={(event) => !isActiveItem && event.currentTarget.classList.remove('bg-light-subtle')}
                                role="menuitem"
                            >
                                { isNewTabItem ? (
                                    <>
                                        { leadingWords && <span>{leadingWords}</span> }
                                        <span className="d-inline-flex align-items-center gap-1 text-nowrap">
                                            <span>{lastWord}</span>
                                            <NewTabIcon className="fs-8" />
                                        </span>
                                    </>
                                ) : (
                                    <span>{item.title}</span>
                                ) }

                                { isSignOutItem && <SignOutIcon className="fs-6" /> }
                            </a>
                        </li>
                    );
                })}
            </ul>
        </div>
    );
}

export default UserNavDropdown;